/**
 * Casino War — server-authoritative rules engine (2–6 players against the dealer, session game with chips).
 *
 * Rules implemented:
 *  - One 52-card deck, reshuffled before every round. The ace is high; suits never matter.
 *  - Each player bets in seat order (or sits the round out) and may add a Tie side bet, which wins `tieBetPays`:1 when
 *    their card ties the dealer's card.
 *  - The dealer and every player get one card face up. A higher player card wins 1:1; a lower one loses.
 *  - On a tie the player chooses: surrender (half the bet comes back) or go to war by adding a raise equal to the bet.
 *    The dealer burns three cards and the dealer and every warring player get one more card. A player card equal to
 *    or higher than the dealer's wins the raise 1:1 and the original bet is a push; a lower card loses both.
 */
import { buildDeck, parseCard, type CardId } from '../../core/cards.js';
import { checkInt, mergeKnown } from '../../core/config.js';
import { shuffle, type Rng } from '../../core/rng.js';
import {
  assertEngine,
  EngineError,
  isPlainObject,
  standingsFromScores,
  type EngineEvent,
  type GameEngine,
  type SeatSetup,
} from '../../core/types.js';

export interface CasinoWarConfig {
  minBet: number;
  maxBet: number;
  minPlayers: number;
  maxPlayers: number;
  /** A winning Tie side bet pays this many times the stake. */
  tieBetPays: number;
  startDelayMs: number;
  dealDelayMs: number;
  handEndDelayMs: number;
}

export type CasinoWarPhase = 'WAITING' | 'BETTING' | 'DEALING' | 'DECIDING' | 'WAR' | 'HAND_END';
export type CasinoWarSeatStatus = 'WAITING' | 'BET' | 'SIT_OUT';
export type CasinoWarChoice = 'WAR' | 'SURRENDER';
export type CasinoWarOutcome = 'WIN' | 'LOSE' | 'WAR_WIN' | 'WAR_LOSE' | 'SURRENDER';

export interface CasinoWarSeat {
  playerId: string;
  chips: number;
  status: CasinoWarSeatStatus;
  bet: number;
  tieBet: number;
  card: CardId | null;
  choice: CasinoWarChoice | null;
  /** The raise added when going to war. */
  raise: number;
  warCard: CardId | null;
  outcome: CasinoWarOutcome | null;
  /** What came back at the end of the round (stake plus winnings, or 0). */
  payout: number;
}

export interface CasinoWarResult {
  handNo: number;
  dealer: CardId;
  dealerWar: CardId | null;
  seats: { seat: number; bet: number; tieBet: number; card: CardId; warCard: CardId | null; outcome: CasinoWarOutcome; payout: number; delta: number }[];
}

export interface CasinoWarState {
  config: CasinoWarConfig;
  seats: (CasinoWarSeat | null)[];
  phase: CasinoWarPhase;
  handNo: number;
  /** Seat to bet, or to choose between war and surrender. */
  turn: number;
  deck: CardId[];
  dealer: CardId | null;
  dealerWar: CardId | null;
  lastResult: CasinoWarResult | null;
  handsPlayed: number;
}

export type CasinoWarAction = { type: 'bet'; amount: number; tieBet: number } | { type: 'sitOut' } | { type: 'war' } | { type: 'surrender' };

export type CasinoWarLegal =
  | { kind: 'bet'; min: number; max: number; canSitOut: boolean; tieBetMax: number }
  | { kind: 'tie'; canWar: boolean; raise: number }
  | { kind: 'none' };

export interface CasinoWarView {
  gameType: 'CASINO_WAR';
  config: CasinoWarConfig;
  seat: number | null;
  phase: CasinoWarPhase;
  handNo: number;
  turn: number;
  dealer: CardId | null;
  dealerWar: CardId | null;
  deckCount: number;
  players: ({ seat: number; playerId: string; chips: number; status: CasinoWarSeatStatus; bet: number; tieBet: number; card: CardId | null; choice: CasinoWarChoice | null; raise: number; warCard: CardId | null; outcome: CasinoWarOutcome | null; payout: number } | null)[];
  lastResult: CasinoWarResult | null;
}

const DEFAULTS: CasinoWarConfig = {
  minBet: 10,
  maxBet: 1000,
  minPlayers: 2,
  maxPlayers: 6,
  tieBetPays: 10,
  startDelayMs: 2000,
  dealDelayMs: 1100,
  handEndDelayMs: 4000,
};

/** Casino War card value: 2 up to the ace at 14. */
export const casinoWarValue = (card: CardId): number => parseCard(card).rank;

function seatsWhere(s: CasinoWarState, pred: (p: CasinoWarSeat, i: number) => boolean): number[] {
  const out: number[] = [];
  s.seats.forEach((p, i) => {
    if (p && pred(p, i)) out.push(i);
  });
  return out;
}

const canStart = (s: CasinoWarState): boolean => seatsWhere(s, (p) => p.chips >= s.config.minBet).length >= s.config.minPlayers;

function advanceBetting(s: CasinoWarState, from: number): void {
  for (let seat = from + 1; seat < s.seats.length; seat++) {
    const p = s.seats[seat];
    if (p && p.status === 'WAITING') {
      s.turn = seat;
      return;
    }
  }
  s.phase = seatsWhere(s, (p) => p.status === 'BET').length > 0 ? 'DEALING' : 'WAITING';
}

/** The next tied seat, after `from`, that still has to choose. */
function nextDecider(s: CasinoWarState, from: number): number {
  for (let seat = from + 1; seat < s.seats.length; seat++) {
    const p = s.seats[seat];
    if (p && p.status === 'BET' && p.outcome === null && p.choice === null) return seat;
  }
  return -1;
}

function startHand(s: CasinoWarState, rng: Rng, events: EngineEvent[]): void {
  s.handNo++;
  s.deck = shuffle(buildDeck(), rng);
  s.dealer = null;
  s.dealerWar = null;
  for (const p of s.seats) {
    if (!p) continue;
    p.bet = 0;
    p.tieBet = 0;
    p.card = null;
    p.choice = null;
    p.raise = 0;
    p.warCard = null;
    p.outcome = null;
    p.payout = 0;
    p.status = p.chips >= s.config.minBet ? 'WAITING' : 'SIT_OUT';
  }
  s.phase = 'BETTING';
  s.turn = -1;
  events.push({ type: 'handStart', handNo: s.handNo });
  advanceBetting(s, -1);
  if (s.phase !== 'BETTING') events.push({ type: 'waitingForPlayers' });
}

function settle(s: CasinoWarState, events: EngineEvent[]): void {
  const cfg = s.config;
  const dealer = casinoWarValue(s.dealer!);
  const seats: CasinoWarResult['seats'] = [];
  s.seats.forEach((p, seat) => {
    if (!p || p.status !== 'BET' || p.card === null) return;
    const v = casinoWarValue(p.card);
    let payout = 0;
    if (p.outcome === null) {
      if (p.choice === 'SURRENDER') {
        p.outcome = 'SURRENDER';
        payout = Math.floor(p.bet / 2);
      } else if (p.choice === 'WAR') {
        const won = casinoWarValue(p.warCard!) >= casinoWarValue(s.dealerWar!);
        p.outcome = won ? 'WAR_WIN' : 'WAR_LOSE';
        payout = won ? p.bet + p.raise * 2 : 0;
      }
    } else {
      payout = p.outcome === 'WIN' ? p.bet * 2 : 0;
    }
    // The Tie side bet is judged on the first cards only.
    if (p.tieBet > 0 && v === dealer) payout += p.tieBet * (1 + cfg.tieBetPays);
    p.payout = payout;
    p.chips += payout;
    seats.push({ seat, bet: p.bet, tieBet: p.tieBet, card: p.card, warCard: p.warCard, outcome: p.outcome!, payout, delta: payout - p.bet - p.raise - p.tieBet });
  });
  s.lastResult = { handNo: s.handNo, dealer: s.dealer!, dealerWar: s.dealerWar, seats };
  s.handsPlayed++;
  s.phase = 'HAND_END';
  s.turn = -1;
  events.push({ type: 'handEnd', handNo: s.handNo, results: seats.map((r) => ({ seat: r.seat, delta: r.delta })) });
}

export const casinoWarEngine: GameEngine<CasinoWarConfig, CasinoWarState, CasinoWarAction, CasinoWarView, CasinoWarLegal> = {
  gameType: 'CASINO_WAR',
  kind: 'session',

  defaultConfig: () => ({ ...DEFAULTS }),

  normalizeConfig(input) {
    const c = mergeKnown(DEFAULTS, input);
    checkInt('maxPlayers', c.maxPlayers, 1, 8);
    checkInt('minPlayers', c.minPlayers, 1, c.maxPlayers);
    checkInt('minBet', c.minBet, 1, 1e9);
    checkInt('maxBet', c.maxBet, c.minBet, 1e12);
    checkInt('tieBetPays', c.tieBetPays, 1, 50);
    checkInt('startDelayMs', c.startDelayMs, 0, 60000);
    checkInt('dealDelayMs', c.dealDelayMs, 0, 60000);
    checkInt('handEndDelayMs', c.handEndDelayMs, 0, 60000);
    return c;
  },

  seatLimits: (c) => ({ min: c.minPlayers, max: c.maxPlayers }),

  createState(config, seats, rng) {
    const s: CasinoWarState = {
      config,
      seats: Array<CasinoWarSeat | null>(config.maxPlayers).fill(null),
      phase: 'WAITING',
      handNo: 0,
      turn: -1,
      deck: shuffle(buildDeck(), rng),
      dealer: null,
      dealerWar: null,
      lastResult: null,
      handsPlayed: 0,
    };
    for (const setup of seats) this.addSeat!(s, setup);
    return s;
  },

  addSeat(s, setup: SeatSetup) {
    assertEngine(setup.seat >= 0 && setup.seat < s.seats.length && !s.seats[setup.seat], 'SEAT_TAKEN', 'Seat unavailable');
    const chips = setup.chips ?? 0;
    assertEngine(Number.isInteger(chips) && chips >= 0, 'INVALID_AMOUNT', 'Invalid chips');
    // While bets are still being taken a later seat can join in; once the cards are coming they wait.
    const open = s.phase === 'WAITING' || s.phase === 'HAND_END' || (s.phase === 'BETTING' && chips >= s.config.minBet);
    s.seats[setup.seat] = { playerId: setup.playerId, chips, status: open ? 'WAITING' : 'SIT_OUT', bet: 0, tieBet: 0, card: null, choice: null, raise: 0, warCard: null, outcome: null, payout: 0 };
    return [{ type: 'playerJoined', seat: setup.seat, playerId: setup.playerId, chips }];
  },

  removeSeat(s, seat) {
    const p = s.seats[seat];
    assertEngine(p, 'INVALID_ACTION', 'Seat empty');
    let chips = p.chips;
    const wasTurn = s.turn === seat;
    if (s.phase === 'BETTING') {
      // Nothing is dealt yet: the bets come back.
      chips += p.bet + p.tieBet;
      s.seats[seat] = null;
      if (wasTurn) advanceBetting(s, seat);
    } else if (s.phase === 'DECIDING' && p.status === 'BET' && p.outcome === null && p.choice === null) {
      // Leaving on a tie surrenders the hand: half the bet comes back.
      chips += Math.floor(p.bet / 2);
      p.choice = 'SURRENDER';
      p.outcome = 'SURRENDER';
      p.payout = Math.floor(p.bet / 2);
      s.seats[seat] = null;
      if (wasTurn) afterDecision(s, seat, []);
    } else {
      // Leaving once the cards are coming forfeits the bets on the table.
      s.seats[seat] = null;
      if (s.phase === 'DECIDING' && wasTurn) afterDecision(s, seat, []);
    }
    return { chips, events: [{ type: 'playerLeft', seat }] };
  },

  parseAction(raw) {
    if (!isPlainObject(raw)) throw new EngineError('INVALID_ACTION', 'Action must be an object');
    if (raw.type === 'sitOut') return { type: 'sitOut' };
    if (raw.type === 'war') return { type: 'war' };
    if (raw.type === 'surrender') return { type: 'surrender' };
    if (raw.type === 'bet' && typeof raw.amount === 'number' && Number.isInteger(raw.amount)) {
      const tieBet = raw.tieBet === undefined ? 0 : raw.tieBet;
      if (typeof tieBet === 'number' && Number.isInteger(tieBet) && tieBet >= 0) return { type: 'bet', amount: raw.amount, tieBet };
    }
    throw new EngineError('INVALID_ACTION', 'Unknown Casino War action');
  },

  applyAction(s, seat, action) {
    const p = s.seats[seat];
    assertEngine(p, 'INVALID_ACTION', 'You are not at this table');
    const events: EngineEvent[] = [];
    if (action.type === 'war' || action.type === 'surrender') {
      assertEngine(s.phase === 'DECIDING', 'INVALID_PHASE', 'There is no tie to settle');
      assertEngine(seat === s.turn, 'NOT_YOUR_TURN', 'Not your turn');
      if (action.type === 'war') {
        assertEngine(p.chips >= p.bet, 'INVALID_AMOUNT', 'Not enough chips to go to war');
        p.chips -= p.bet;
        p.raise = p.bet;
        p.choice = 'WAR';
        events.push({ type: 'war', seat, amount: p.raise });
      } else {
        p.choice = 'SURRENDER';
        events.push({ type: 'surrender', seat });
      }
      afterDecision(s, seat, events);
      return events;
    }
    assertEngine(s.phase === 'BETTING', 'INVALID_PHASE', 'Betting is closed');
    assertEngine(seat === s.turn, 'NOT_YOUR_TURN', 'Not your turn');
    if (action.type === 'sitOut') {
      p.status = 'SIT_OUT';
      events.push({ type: 'sitOut', seat });
      advanceBetting(s, seat);
      return events;
    }
    const max = Math.min(s.config.maxBet, p.chips);
    assertEngine(action.amount >= s.config.minBet && action.amount <= max, 'INVALID_AMOUNT', `Bet between ${s.config.minBet} and ${max}`);
    assertEngine(action.tieBet <= s.config.maxBet && action.amount + action.tieBet <= p.chips, 'INVALID_AMOUNT', 'Tie bet is too large');
    p.chips -= action.amount + action.tieBet;
    p.bet = action.amount;
    p.tieBet = action.tieBet;
    p.status = 'BET';
    events.push({ type: 'bet', seat, amount: action.amount, tieBet: action.tieBet });
    advanceBetting(s, seat);
    return events;
  },

  getActors(s) {
    return (s.phase === 'BETTING' || s.phase === 'DECIDING') && s.turn >= 0 && s.seats[s.turn] ? [s.turn] : [];
  },

  getLegalActions(s, seat) {
    const p = s.seats[seat];
    if (!p || seat !== s.turn) return { kind: 'none' };
    if (s.phase === 'BETTING') {
      const max = Math.min(s.config.maxBet, p.chips);
      return { kind: 'bet', min: s.config.minBet, max, canSitOut: true, tieBetMax: Math.max(0, Math.min(s.config.maxBet, p.chips - s.config.minBet)) };
    }
    if (s.phase === 'DECIDING') return { kind: 'tie', canWar: p.chips >= p.bet, raise: p.bet };
    return { kind: 'none' };
  },

  getTimeoutAction(s) {
    return s.phase === 'DECIDING' ? { type: 'surrender' } : { type: 'sitOut' };
  },

  getView(s, seat) {
    return {
      gameType: 'CASINO_WAR',
      config: s.config,
      seat,
      phase: s.phase,
      handNo: s.handNo,
      turn: s.turn,
      dealer: s.dealer,
      dealerWar: s.dealerWar,
      deckCount: s.deck.length,
      players: s.seats.map((p, i) => (p ? { seat: i, playerId: p.playerId, chips: p.chips, status: p.status, bet: p.bet, tieBet: p.tieBet, card: p.card, choice: p.choice, raise: p.raise, warCard: p.warCard, outcome: p.outcome, payout: p.payout } : null)),
      lastResult: s.lastResult ? structuredClone(s.lastResult) : null,
    };
  },

  getSystemStep(s) {
    if (s.phase === 'DEALING' || s.phase === 'WAR') return { kind: 'deal', delayMs: s.config.dealDelayMs };
    if (s.phase === 'HAND_END') return { kind: 'nextHand', delayMs: s.config.handEndDelayMs };
    if (s.phase === 'WAITING' && canStart(s)) return { kind: 'startHand', delayMs: s.config.startDelayMs };
    return null;
  },

  runSystemStep(s, rng) {
    const events: EngineEvent[] = [];
    if (s.phase === 'DEALING') {
      // The dealer card, then one card to each player who bet, all face up.
      if (s.deck.length < 20) s.deck = shuffle(buildDeck(), rng);
      s.dealer = s.deck.pop()!;
      events.push({ type: 'cardDealt', to: 'DEALER', card: s.dealer });
      const dv = casinoWarValue(s.dealer);
      for (const [seat, p] of s.seats.entries()) {
        if (!p || p.status !== 'BET') continue;
        p.card = s.deck.pop()!;
        events.push({ type: 'cardDealt', to: seat, card: p.card });
        const v = casinoWarValue(p.card);
        p.outcome = v > dv ? 'WIN' : v < dv ? 'LOSE' : null;
      }
      const first = nextDecider(s, -1);
      if (first >= 0) {
        s.phase = 'DECIDING';
        s.turn = first;
      } else settle(s, events);
      return events;
    }
    if (s.phase === 'WAR') {
      // Three burned cards, then one card for the dealer and one for every player who went to war.
      s.deck.splice(-3, 3);
      s.dealerWar = s.deck.pop()!;
      events.push({ type: 'cardDealt', to: 'DEALER', card: s.dealerWar });
      for (const [seat, p] of s.seats.entries()) {
        if (!p || p.choice !== 'WAR') continue;
        p.warCard = s.deck.pop()!;
        events.push({ type: 'cardDealt', to: seat, card: p.warCard });
      }
      settle(s, events);
      return events;
    }
    if (s.phase === 'HAND_END' || s.phase === 'WAITING') {
      s.phase = 'WAITING';
      if (canStart(s)) startHand(s, rng, events);
      else events.push({ type: 'waitingForPlayers' });
    }
    return events;
  },

  isFinished: () => false,
  isHandInProgress: (s) => s.phase === 'DEALING' || s.phase === 'DECIDING' || s.phase === 'WAR',
  getChips: (s) => Object.fromEntries(seatsWhere(s, () => true).map((i) => [i, s.seats[i]!.chips])),
  getStandings: (s) => standingsFromScores(seatsWhere(s, () => true).map((seat) => ({ seat, score: s.seats[seat]!.chips }))),
  getRoundsCompleted: (s) => s.handsPlayed,
};

/** After a tie choice: next tied seat decides, otherwise war cards are dealt or the round settles. */
function afterDecision(s: CasinoWarState, from: number, events: EngineEvent[]): void {
  const next = nextDecider(s, from);
  if (next >= 0) {
    s.turn = next;
    return;
  }
  s.turn = -1;
  if (seatsWhere(s, (p) => p.choice === 'WAR').length > 0) s.phase = 'WAR';
  else settle(s, events);
}
